/**
 * useConversationStatus Hook
 * 
 * Verwaltet den Status (Offen/Später/Erledigt) von Konversationen.
 * Persistiert in Supabase (conversation_status, conversation_notes, conversation_assignments)
 * mit localStorage als Fallback/Cache.
 * 
 * Funktionen:
 * - Status setzen (offen, spaeter, erledigt)
 * - Status abrufen
 * - Notizen zu Konversationen
 * - Mitarbeiter-Zuordnung
 */

import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { supabase } from '../lib/supabase';

export type ConversationStatusType = 'offen' | 'spaeter' | 'erledigt';

export interface ConversationStatusEntry {
  conversationId: string;
  status: ConversationStatusType;
  assignedTo?: string[];
  notes?: ConversationNote[];
  updatedAt: string;
  updatedBy?: string;
  updatedByName?: string;
}

export interface ConversationNote {
  id: string;
  authorId: string;
  authorName: string;
  content: string;
  mentions: string[];
  createdAt: string;
}

export interface ConversationAssignment {
  conversationId: string;
  assignedTo: string;
  assignedBy: string;
  assignedAt: string;
}

const STORAGE_KEY = 'koop_conversation_status';
const NOTES_KEY = 'koop_conversation_notes';
const ASSIGNMENTS_KEY = 'koop_conversation_assignments';

function loadFromStorage<T>(key: string, defaultValue: T): T {
  try {
    const stored = localStorage.getItem(key);
    if (stored) return JSON.parse(stored);
  } catch (e) {
    console.error(`Error loading ${key} from localStorage:`, e);
  }
  return defaultValue;
}

function saveToStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    console.error(`Error saving ${key} to localStorage:`, e);
  }
}

export function useConversationStatus() {
  const [statusMap, setStatusMap] = useState<Record<string, ConversationStatusEntry>>(() => 
    loadFromStorage(STORAGE_KEY, {})
  );
  const [notesMap, setNotesMap] = useState<Record<string, ConversationNote[]>>(() => 
    loadFromStorage(NOTES_KEY, {})
  );
  const [assignmentsMap, setAssignmentsMap] = useState<Record<string, ConversationAssignment[]>>(() => 
    loadFromStorage(ASSIGNMENTS_KEY, {})
  );

  const initialLoadDone = useRef(false);

  // --- Supabase Laden beim Start ---
  useEffect(() => {
    if (initialLoadDone.current) return;
    initialLoadDone.current = true;

    const loadFromSupabase = async () => {
      try {
        // Status laden
        const { data: statusData } = await supabase
          .from('conversation_status')
          .select('*');
        
        if (statusData && statusData.length > 0) {
          const map: Record<string, ConversationStatusEntry> = {};
          for (const row of statusData) {
            map[row.id] = {
              conversationId: row.id,
              status: row.status as ConversationStatusType,
              updatedAt: row.updated_at,
              updatedByName: row.updated_by && row.updated_by !== 'user' ? row.updated_by : undefined,
            };
          }
          setStatusMap(prev => ({ ...prev, ...map }));
          saveToStorage(STORAGE_KEY, { ...loadFromStorage(STORAGE_KEY, {}), ...map });
        }

        // Notizen laden
        const { data: notesData } = await supabase
          .from('conversation_notes')
          .select('*')
          .order('created_at', { ascending: true });
        
        if (notesData && notesData.length > 0) {
          const map: Record<string, ConversationNote[]> = {};
          for (const row of notesData) {
            const note: ConversationNote = {
              id: row.id,
              authorId: row.author,
              authorName: row.author,
              content: row.content,
              mentions: row.mentions || [],
              createdAt: row.created_at,
            };
            if (!map[row.conversation_id]) map[row.conversation_id] = [];
            map[row.conversation_id].push(note);
          }
          setNotesMap(prev => ({ ...prev, ...map }));
          saveToStorage(NOTES_KEY, { ...loadFromStorage(NOTES_KEY, {}), ...map });
        }

        // Zuordnungen laden
        const { data: assignData } = await supabase
          .from('conversation_assignments')
          .select('*');
        
        if (assignData && assignData.length > 0) {
          const map: Record<string, ConversationAssignment[]> = {};
          for (const row of assignData) {
            const assignment: ConversationAssignment = {
              conversationId: row.conversation_id,
              assignedTo: row.assigned_to,
              assignedBy: row.assigned_by,
              assignedAt: row.assigned_at,
            };
            if (!map[row.conversation_id]) map[row.conversation_id] = [];
            map[row.conversation_id].push(assignment);
          }
          setAssignmentsMap(prev => ({ ...prev, ...map }));
          saveToStorage(ASSIGNMENTS_KEY, { ...loadFromStorage(ASSIGNMENTS_KEY, {}), ...map });
        }
      } catch (err) {
        console.warn('Supabase load failed, using localStorage fallback:', err);
      }
    };

    loadFromSupabase();
  }, []);

  // --- localStorage Sync ---
  useEffect(() => {
    saveToStorage(STORAGE_KEY, statusMap);
  }, [statusMap]);

  useEffect(() => {
    saveToStorage(NOTES_KEY, notesMap);
  }, [notesMap]);

  useEffect(() => {
    saveToStorage(ASSIGNMENTS_KEY, assignmentsMap);
  }, [assignmentsMap]);

  // --- Status ---
  const getStatus = useCallback((conversationId: string): ConversationStatusType => {
    return statusMap[conversationId]?.status || 'offen';
  }, [statusMap]);

  const getStatusEntry = useCallback((conversationId: string): ConversationStatusEntry | null => {
    return statusMap[conversationId] || null;
  }, [statusMap]);

  const setStatus = useCallback((conversationId: string, status: ConversationStatusType, updatedBy?: string, updatedByName?: string) => {
    setStatusMap(prev => ({
      ...prev,
      [conversationId]: {
        ...prev[conversationId],
        conversationId,
        status,
        updatedAt: new Date().toISOString(),
        updatedBy: updatedBy || prev[conversationId]?.updatedBy,
        updatedByName: updatedByName || prev[conversationId]?.updatedByName,
      }
    }));

    // Supabase upsert (fire-and-forget)
    supabase
      .from('conversation_status')
      .upsert({
        id: conversationId,
        status,
        updated_at: new Date().toISOString(),
        updated_by: updatedByName || updatedBy || 'user',
      }, { onConflict: 'id' })
      .then(({ error }) => {
        if (error) console.warn('Supabase status upsert failed:', error);
      });
  }, []);

  const setStatusBulk = useCallback((conversationIds: string[], status: ConversationStatusType) => {
    setStatusMap(prev => {
      const updated = { ...prev };
      for (const id of conversationIds) {
        updated[id] = {
          ...prev[id],
          conversationId: id,
          status,
          updatedAt: new Date().toISOString(),
        };
      }
      return updated;
    });

    // Supabase bulk upsert
    const rows = conversationIds.map(id => ({
      id,
      status,
      updated_at: new Date().toISOString(),
      updated_by: 'user',
    }));
    supabase
      .from('conversation_status')
      .upsert(rows, { onConflict: 'id' })
      .then(({ error }) => {
        if (error) console.warn('Supabase bulk status upsert failed:', error);
      });
  }, []);

  // --- Notizen ---
  const addNote = useCallback((
    conversationId: string,
    authorId: string,
    authorName: string,
    content: string,
    mentions: string[] = []
  ) => {
    const note: ConversationNote = {
      id: `note-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
      authorId,
      authorName,
      content,
      mentions,
      createdAt: new Date().toISOString(),
    };
    setNotesMap(prev => ({
      ...prev,
      [conversationId]: [...(prev[conversationId] || []), note],
    }));

    // Supabase insert
    supabase
      .from('conversation_notes')
      .insert({
        id: note.id,
        conversation_id: conversationId,
        content,
        author: authorName,
        mentions,
        created_at: note.createdAt,
      })
      .then(({ error }) => {
        if (error) console.warn('Supabase note insert failed:', error);
      });

    return note;
  }, []);

  const getNotes = useCallback((conversationId: string): ConversationNote[] => {
    return notesMap[conversationId] || [];
  }, [notesMap]);

  const deleteNote = useCallback((conversationId: string, noteId: string) => {
    setNotesMap(prev => ({
      ...prev,
      [conversationId]: (prev[conversationId] || []).filter(n => n.id !== noteId),
    }));

    // Supabase delete
    supabase
      .from('conversation_notes')
      .delete()
      .eq('id', noteId)
      .then(({ error }) => {
        if (error) console.warn('Supabase note delete failed:', error);
      });
  }, []);

  // --- Zuordnungen ---
  const assignTo = useCallback((
    conversationId: string,
    assignedTo: string,
    assignedBy: string
  ) => {
    const assignment: ConversationAssignment = {
      conversationId,
      assignedTo,
      assignedBy,
      assignedAt: new Date().toISOString(),
    };
    setAssignmentsMap(prev => {
      const existing = prev[conversationId] || [];
      if (existing.some(a => a.assignedTo === assignedTo)) return prev;
      return {
        ...prev,
        [conversationId]: [...existing, assignment],
      };
    });

    // Supabase insert (ignore conflict)
    supabase
      .from('conversation_assignments')
      .insert({
        conversation_id: conversationId,
        assigned_to: assignedTo,
        assigned_by: assignedBy,
        assigned_at: assignment.assignedAt,
      })
      .then(({ error }) => {
        if (error && !error.message?.includes('duplicate')) {
          console.warn('Supabase assignment insert failed:', error);
        }
      });
  }, []);

  const unassign = useCallback((conversationId: string, assignedTo: string) => {
    setAssignmentsMap(prev => ({
      ...prev,
      [conversationId]: (prev[conversationId] || []).filter(a => a.assignedTo !== assignedTo),
    }));

    // Supabase delete
    supabase
      .from('conversation_assignments')
      .delete()
      .eq('conversation_id', conversationId)
      .eq('assigned_to', assignedTo)
      .then(({ error }) => {
        if (error) console.warn('Supabase assignment delete failed:', error);
      });
  }, []);

  const getAssignments = useCallback((conversationId: string): ConversationAssignment[] => {
    return assignmentsMap[conversationId] || [];
  }, [assignmentsMap]);

  // --- Statistiken ---
  const stats = useMemo(() => {
    const entries = Object.values(statusMap);
    return {
      offen: entries.filter(e => e.status === 'offen').length,
      spaeter: entries.filter(e => e.status === 'spaeter').length,
      erledigt: entries.filter(e => e.status === 'erledigt').length,
      total: entries.length,
    };
  }, [statusMap]);

  return {
    getStatus,
    getStatusEntry,
    setStatus,
    setStatusBulk,
    addNote,
    getNotes,
    deleteNote,
    assignTo,
    unassign,
    getAssignments,
    stats,
    statusMap,
    notesMap,
    assignmentsMap,
  };
}
